/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/10/22, 8:29 PM
 *
 */

import { Fragment, useRef, useState } from 'react';
import LetteredAvatar from 'react-avatar';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Link, useLocation } from 'react-router-dom';
import ReactToPrint from 'react-to-print';
import { Dialog, Menu, Transition } from '@headlessui/react';
import {
  Camera,
  CircleWavyCheck,
  CircleWavyWarning,
  DotsThreeOutline,
  Envelope,
  MapPin,
  UserCircle,
} from 'phosphor-react';
import { useAppDispatch, useAppSelector } from 'stores';
import { Member } from 'types/MemberTypes';
import { Role } from 'types/RoleTypes';

import { removeAuth } from 'features/auth';
// import { logoutUser } from '@/services/requests';
import { changePassword } from 'features/auth/api/requests/auth.request';

import ChangePassword from 'assets/img/change-password.svg';

import { alert } from 'components/Alert';
import Button from 'components/Button';
import Input from 'components/Input';
import MemberModal from 'features/members/components/modal/MemberModal';
import MemberOtherDetailModal from 'features/members/components/modal/MemberOtherDetailModal';
import ProfileImageModal from 'features/members/components/modal/ProfileImageModal';

import MemberProfileToPrint from './MemberProfilePrint';

interface IMemberProfileHeaderProps {
  member: Member;
  role: Role;
}

type ChangePasswordFormData = {
  newPassword: string;
  oldPassword: string;
  confirmNewPassword: string;
};

/**
 *
 * @param {Member} member - selected Member
 * @param {Member} role - selected Role
 * @return {JSX.Element}
 */
export default function MemberProfileHeader({ member, role }: IMemberProfileHeaderProps) {
  const componentRef = useRef<HTMLDivElement>(null);

  const { handleSubmit, register, reset } = useForm<ChangePasswordFormData>();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);

  const user = useAppSelector((state) => state.auth.user);
  const dispatch = useAppDispatch();

  const onLogOut = () => {
    dispatch(removeAuth());
    toast.loading('Logging Out!');
    if (!user) {
      const redirect =
        process.env.REACT_APP_ENVIRONMENT === 'DEVELOPMENT'
          ? 'https://dev.app.sunya.health'
          : 'https://app.sunya.health';
      window.open(redirect, '_self');
    }
  };

  return (
    <>
      <div className="hidden">
        <MemberProfileToPrint member={member} ref={componentRef} />
      </div>
      <div className="flex justify-between md:flex-col md:gap-8">
        <div className="flex gap-8 sm:flex-col">
          {location.pathname.includes('profile') && user ? (
            <ProfileImageModal selectedMember={user}>
              <div className="relative h-56 w-56 flex-shrink-0 cursor-pointer rounded-xl">
                {user?.image ? (
                  <img
                    src={user?.image}
                    className="h-56 w-56 rounded-xl object-cover shadow-sm"
                    alt="profile image"
                  />
                ) : (
                  <LetteredAvatar
                    name={user?.name}
                    size="100%"
                    className="overflow-hidden rounded-xl"
                    maxInitials={2}
                  />
                )}

                <div
                  className={`h-6 w-6 shadow-xl ${
                    user?.active ? 'bg-primary-500' : 'bg-red-500'
                  } absolute inset-y-1/2 -right-3 z-20 rounded-full ring-[3px] ring-white`}
                />
                <div className="absolute -right-4 -top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white text-3xl text-gray-850">
                  <Camera weight="duotone" />
                </div>
              </div>
            </ProfileImageModal>
          ) : (
            <div className="relative h-56 w-56  flex-shrink-0 rounded-xl">
              {member.image ? (
                <img
                  src={member.image}
                  className="rounded-xl object-cover shadow-sm"
                  alt="profile image"
                />
              ) : (
                <LetteredAvatar
                  name={member.name}
                  size="100%"
                  className="overflow-hidden rounded-xl"
                  maxInitials={2}
                />
              )}
              <div
                className={`h-6 w-6 shadow-xl ${
                  member.active ? 'bg-primary-500' : 'bg-red-500'
                } absolute inset-y-1/2 -right-3 z-20 rounded-full ring-[3px] ring-white`}
              />
            </div>
          )}

          <div className="flex w-full flex-col justify-between py-1.5 md:gap-4">
            <div className="flex w-full items-center justify-between">
              <div className="flex flex-col gap-2 md:gap-1">
                <div className="flex items-center  gap-2">
                  <h1 className="text-3xl font-bold text-gray-900 line-clamp-1">{member.name}</h1>
                  {member.verified ? (
                    <CircleWavyCheck size={24} weight="duotone" className="text-primary-600" />
                  ) : (
                    <CircleWavyWarning size={24} weight="duotone" className="text-red-600" />
                  )}
                </div>

                <div className="flex gap-4 lg:flex-col md:gap-0">
                  <div className="flex items-start space-x-1">
                    <UserCircle size={18} weight="duotone" color="rgb(163 163 163)" />
                    <span className="text-lg font-medium text-gray-400">{role.name}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <MapPin size={18} weight="duotone" color="rgb(163 163 163)" />
                    <span className="text-lg font-medium text-gray-400">{member.address}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Envelope size={18} weight="duotone" color="rgb(163 163 163)" />
                    <span className="text-lg font-medium text-gray-400">{member.email}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          {user?.id === 1 || user?.role.slug.includes('admin') ? (
            <>
              <div className="relative flex flex-shrink-0 space-x-4 self-start">
                <MemberOtherDetailModal
                  otherDetails={member.details}
                  memberData={member}
                  selectedRole={role}
                >
                  <Button color="secondary" size="sm">
                    Edit Other Details
                  </Button>
                </MemberOtherDetailModal>
                <MemberModal
                  type="edit"
                  selectedRole={role}
                  initialData={member}
                  button={<Button size="sm">Edit Profile</Button>}
                />

                <Transition appear show={isOpen} as={Fragment} data-testid="modal">
                  <Dialog
                    as="div"
                    open={isOpen}
                    className="fixed inset-0 z-50"
                    onClose={() => setIsOpen(false)}
                  >
                    <Dialog.Overlay className="fixed inset-0 bg-black opacity-60" />
                    <div className="min-h-screen text-center md:px-16 sm:px-4">
                      <Transition.Child as={Fragment}>
                        <Dialog.Overlay className="fixed inset-0" />
                      </Transition.Child>

                      {/* This element is to trick the browser into centering the modal contents. */}
                      <span className="inline-block h-screen align-middle" aria-hidden="true">
                        &#8203;
                      </span>
                      <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-100"
                        enterFrom="opacity-0 scale-95 -translate-y-32"
                        enterTo="opacity-100 scale-100 translate-y-0"
                        leave="ease-in duration-75"
                        leaveFrom="opacity-100 scale-100 translate-y-0"
                        leaveTo="opacity-0 scale-90 -translate-y-32"
                      >
                        <div className="sidebar inline-block w-full max-w-6xl transform space-y-8 overflow-hidden rounded-2xl bg-white p-10 text-left align-middle shadow-E600 transition-all sm:p-6">
                          <div className="flex flex-col space-y-16 sm:-space-y-12">
                            <h1 className="text-4xl font-medium text-gray-800">Change Password</h1>
                            <div className="flex w-full items-center space-x-8 sm:flex-col-reverse">
                              <form
                                onSubmit={handleSubmit(async (values) => {
                                  if (values.oldPassword === values.newPassword) {
                                    toast.error('Old Password and New Password cannot be Same');
                                    return;
                                  }
                                  if (values.newPassword !== values.confirmNewPassword) {
                                    toast.error(
                                      "New Password and Confirm New Password Doesn't Match"
                                    );
                                    return;
                                  }

                                  await alert({
                                    type: 'promise',
                                    promise: changePassword(
                                      values.oldPassword,
                                      values.newPassword
                                    ).then(() => {
                                      setIsOpen(false);
                                      reset();
                                    }),
                                    msgs: {
                                      loading: 'Changing Password',
                                      success: 'Password Changed Successfully',
                                    },
                                    id: 'change-password-modal',
                                  });
                                })}
                                className="w-1/2 space-y-16 sm:w-full sm:space-y-8"
                              >
                                <div className="space-y-8">
                                  <Input
                                    label="Old Password"
                                    type="password"
                                    placeholder="Enter Old Password"
                                    {...register('oldPassword')}
                                  />
                                  <Input
                                    label="New Password"
                                    type="password"
                                    placeholder="Enter New Password"
                                    {...register('newPassword')}
                                  />
                                  <Input
                                    label="Confirm New Password"
                                    type="password"
                                    placeholder="Confirm New Password"
                                    {...register('confirmNewPassword')}
                                  />
                                </div>

                                <div className="flex space-x-4">
                                  <Button>Change</Button>

                                  <Button color="error" onClick={() => setIsOpen(false)}>
                                    Cancel
                                  </Button>
                                </div>
                              </form>

                              <div className="-mt-40 flex w-1/2 flex-col items-center -space-y-6 sm:mt-10  sm:w-full sm:space-y-0">
                                <div className="relative h-8xl w-full sm:-ml-12 sm:h-7xl  ">
                                  <img src={ChangePassword} className="object-contain" />
                                </div>
                                <div className="flex flex-col items-center sm:hidden ">
                                  <h1 className="text-2xl font-medium text-primary-600 sm:text-xl">
                                    New Password must contain
                                  </h1>
                                  <div className=" flex flex-col items-center text-xl font-medium text-gray-600 sm:items-start sm:text-base">
                                    <p>At least six characters</p>
                                    <p>At least one uppercase character</p>
                                    <p>At least one number </p>
                                    <p>At least one special character </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Transition.Child>
                    </div>
                  </Dialog>
                </Transition>

                {(user?.id === 1 || location.pathname === '/profile') && (
                  <Menu className="z-20" as="div">
                    <Menu.Button>
                      <button className=" rounded-lg bg-slate-200 py-3.5 px-4 text-xl text-primary-600 hover:bg-gray-300">
                        <DotsThreeOutline weight="duotone" size={20} />
                      </button>
                    </Menu.Button>{' '}
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items
                        id="menu-items"
                        className="absolute top-16 right-0 mt-2 w-72 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                      >
                        <>
                          <div className="py-2" onClick={() => setIsOpen(true)}>
                            <Menu.Item as="div">
                              {({ active: btnActive }) => (
                                <button
                                  className={`${
                                    btnActive ? `bg-primary-50 text-primary-500 ` : 'text-gray-700'
                                  } group flex w-full items-center rounded-md px-4 py-3 text-lg font-medium`}
                                >
                                  Change Password
                                </button>
                              )}
                            </Menu.Item>
                          </div>

                          <div className="py-2" onClick={onLogOut}>
                            <Menu.Item as="div">
                              {({ active: btnActive }) => (
                                <button
                                  className={`${
                                    btnActive ? `bg-primary-50 text-primary-500 ` : 'text-gray-700'
                                  } group flex w-full items-center rounded-md px-4 py-3 text-lg font-medium`}
                                >
                                  Log out
                                </button>
                              )}
                            </Menu.Item>
                          </div>
                        </>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                )}
              </div>
              <div className=" mt-4 self-end">
                <div className="mb-4">
                  <ReactToPrint
                    trigger={() => <Button size="sm">Print Profile</Button>}
                    content={() => componentRef.current}
                  />
                </div>
              </div>
              {member.role.slug !== 'school_admin' && (
                <div className="self-end">
                  <Link to={`/members/${member.role.slug}/test_report`}>
                    <Button size="sm">Test Report</Button>
                  </Link>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
